import * as React from 'react';
import { useIntl } from 'react-intl';
import { useRecoilState } from 'recoil';
import { Controller, useForm, useWatch } from 'react-hook-form';
import {
  BOTH_KEY,
  DAILY_KEY,
  ESCAPE_KEY,
  MONTHLY_KEY,
  NONE_KEY,
  PRIVATE_SECTION_KEY,
  PUBLIC_KEY,
  TOTAL_KEY,
  WEEKLY_KEY,
} from '../constants';
import useKeyPress from '../../../hooks/useKeyPress';
import useOutsideClick from '../../../hooks/useOutsideClick';
import SettingsSwitch from './SettingsSwitch';
import { ITableSettings, ITableSettingsData, ITableSummaryData } from '../interfaces';
import { isSummarySettingsMenuOpenedAtom } from '../TripsOverviewState';
import { transformErrors } from '../../../Services/HookFormService';
import useAnalytics from '../../../hooks/useAnalytics';

interface IProps {
  handleSummary: (data: ITableSummaryData) => void;
  data: ITableSettingsData;
  switchSummarySettings: (value: boolean) => void;
}

const getIntervalValue = (value: string) => {
  switch (value) {
    case '1':
      return TOTAL_KEY;
    case '2':
      return MONTHLY_KEY;
    case '3':
      return WEEKLY_KEY;
    case '4':
      return DAILY_KEY;
    default:
      return NONE_KEY;
  }
};

const getIntervalItem = (item: string) => {
  switch (item) {
    case TOTAL_KEY:
      return '1';
    case MONTHLY_KEY:
      return '2';
    case WEEKLY_KEY:
      return '3';
    case DAILY_KEY:
      return '4';
    default:
      return '0';
  }
};

function SummarySettings(props: IProps) {
  const { formatMessage: f } = useIntl();
  const [isSummarySettingsMenuOpened, setIsSummarySettingsMenuOpened] = useRecoilState(
    isSummarySettingsMenuOpenedAtom
  );
  const { trackEvent } = useAnalytics();

  const clickOutsideRef: React.RefObject<HTMLDivElement> = React.createRef();

  const defaultValues = React.useMemo(
    () => ({
      show: props.data?.overviewSummary ? '1' : '0',
      interval: getIntervalItem(
        props.data?.periodicSummaries?.periodicity
          ? props.data?.periodicSummaries?.periodicity
          : '0'
      ),
      private: !!(
        props.data?.periodicSummaries?.displayData &&
        props.data?.periodicSummaries?.displayData.includes(PRIVATE_SECTION_KEY)
      ),
      public: !!(
        props.data?.periodicSummaries?.displayData &&
        props.data?.periodicSummaries?.displayData.includes(PUBLIC_KEY)
      ),
      all: !!(
        props.data?.periodicSummaries?.displayData &&
        props.data?.periodicSummaries?.displayData.includes(BOTH_KEY)
      ),
    }),
    [props.data]
  );

  const {
    control,
    handleSubmit,
    setValue,
    trigger,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    mode: 'onChange',
    defaultValues,
    resolver: async (values) => {
      const validateErrors: Record<string, string> = {};

      if (!values.private && !values.public && !values.all && values.interval !== '0') {
        validateErrors.all = `${f({ id: 'tables.tripsOverview.summarySettings.validation' })}`;
      }
      const formErrors = transformErrors(validateErrors);

      return { values, errors: formErrors };
    },
  });

  const watchedValues = useWatch({
    control,
  });

  const isDirty = React.useMemo(() => {
    return (
      watchedValues.show !== defaultValues.show ||
      watchedValues.interval !== defaultValues.interval ||
      watchedValues.private !== defaultValues.private ||
      watchedValues.public !== defaultValues.public ||
      watchedValues.all !== defaultValues.all
    );
  }, [watchedValues, defaultValues]);

  useOutsideClick(clickOutsideRef, () => {
    setIsSummarySettingsMenuOpened(false);
  });

  useKeyPress(ESCAPE_KEY, () => {
    setIsSummarySettingsMenuOpened(false);
  });

  const handleApply = (data: ITableSettings) => {
    const displayData: string[] = [];
    if (data.all) {
      displayData.push(BOTH_KEY);
    }
    if (data.public) {
      displayData.push(PUBLIC_KEY);
    }
    if (data.private) {
      displayData.push(PRIVATE_SECTION_KEY);
    }
    const result = {
      overviewSummary: data.show === '1',
      periodicSummaries: {
        displayData,
        periodicity: getIntervalValue(data.interval),
      },
    };
    props.handleSummary(result);
    trackEvent(['ride book', 'click', 'Apply ride book summary settings']);
  };

  const handleReset = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.preventDefault();
    reset(defaultValues);
    trackEvent(['ride book', 'click', 'Reset ride book summary settings']);
  };

  const handleSummarySettingsMenu = (event: React.MouseEvent<HTMLDivElement>) => {
    const { id } = event.target as HTMLDivElement;
    trackEvent(['ride book', 'click', 'Open ride book summary settings menu']);
    if (id === 'summary-settings-menu' || id === 'summary-settings-menu-title') {
      setIsSummarySettingsMenuOpened(!isSummarySettingsMenuOpened);
    }
  };

  return (
    <div ref={clickOutsideRef} className="col-12 col-md-auto mr-md-2 mb-2">
      <div
        className={`b-dropdown b-dropdown--switcher ${isSummarySettingsMenuOpened ? 'open' : ''}`}
        style={{ width: '240px' }}
        title={f({ id: 'tables.tripsOverview.summarySettings.general.tooltip' })}
      >
        <div
          onClick={handleSummarySettingsMenu}
          className="form-control-labels"
          data-cy="ride-book-open-table-summary-settings"
          id="summary-settings-menu"
        >
          <SettingsSwitch
            changeCallback={props.switchSummarySettings}
            tooltip={f({ id: 'tables.tripsOverview.summarySettings.switch.tooltip' })}
          />
          <span className="ml-2" id="summary-settings-menu-title">
            {f({ id: 'tables.tripsOverview.column.summaryLines' })}
          </span>
        </div>
        {isSummarySettingsMenuOpened && (
          <div className="col-auto">
            <div className="b-dropdown open" style={{ width: '240px', marginLeft: '-14px' }}>
              <div className="b-dropdown__content" style={{ position: 'absolute', zIndex: 999 }}>
                <div style={{ overflow: 'auto', maxHeight: 'calc(100vh - 260px)' }}>
                  <div
                    className="b-dropdown__title b-dropdown__title--border-0"
                    title={f({ id: 'tripsoverview.totalsummary.title.tooltip' })}
                  >
                    {f({ id: 'tripsoverview.totalsummary.title' })}
                  </div>

                  <Controller
                    name="show"
                    control={control}
                    render={({ field }) => (
                      <ul className="b-dropdown__list">
                        <li>
                          <div className="e-radio">
                            <label>
                              <input
                                checked={field.value === '1'}
                                {...field}
                                type="radio"
                                value="1"
                                data-cy="ride-book-table-summaries-settings-total-summary-show"
                              />
                              <span>{f({ id: 'tables.tripsOverview.summaryLines.show' })}</span>
                            </label>
                          </div>
                        </li>
                        <li>
                          <div className="e-radio">
                            <label>
                              <input
                                checked={field.value === '0'}
                                {...field}
                                type="radio"
                                value="0"
                                data-cy="ride-book-table-summaries-settings-total-summary-off"
                              />
                              <span>{f({ id: 'tables.tripsOverview.summaryLines.dontShow' })}</span>
                            </label>
                          </div>
                        </li>
                      </ul>
                    )}
                  />

                  <div
                    className="b-dropdown__title b-dropdown__title--border-0 mt-4"
                    title={f({
                      id: 'tables.tripsOverview.summaryLines.partialSummaries.tooltip',
                    })}
                  >
                    {f({ id: 'tables.tripsOverview.summaryLines.partialSummaries' })}
                  </div>

                  <Controller
                    name="interval"
                    control={control}
                    render={({ field }) => (
                      <ul className="b-dropdown__list">
                        <li>
                          <div className="e-radio">
                            <label>
                              <input
                                checked={field.value === '0'}
                                {...field}
                                type="radio"
                                value="0"
                                data-cy="ride-book-table-summaries-settings-parial-summaries-off"
                              />
                              <span>{f({ id: 'tables.tripsOverview.summaryLines.dontShow' })}</span>
                            </label>
                          </div>
                        </li>
                        <li>
                          <div className="e-radio">
                            <label>
                              <input
                                checked={field.value === '1'}
                                {...field}
                                type="radio"
                                value="1"
                                data-cy="ride-book-table-summaries-settings-parial-summaries-total"
                              />
                              <span>{f({ id: 'tables.tripsOverview.summaryLines.total' })}</span>
                            </label>
                          </div>
                        </li>
                        <li>
                          <div className="e-radio">
                            <label>
                              <input
                                checked={field.value === '2'}
                                {...field}
                                type="radio"
                                value="2"
                                data-cy="ride-book-table-summaries-settings-parial-summaries-month"
                              />
                              <span>
                                {f({ id: 'tables.tripsOverview.summaryLines.afterMonths' })}
                              </span>
                            </label>
                          </div>
                        </li>
                        <li>
                          <div className="e-radio">
                            <label>
                              <input
                                checked={field.value === '3'}
                                {...field}
                                type="radio"
                                value="3"
                                data-cy="ride-book-table-summaries-settings-parial-summaries-weeks"
                              />
                              <span>
                                {f({ id: 'tables.tripsOverview.summaryLines.afterWeeks' })}
                              </span>
                            </label>
                          </div>
                        </li>
                        <li>
                          <div className="e-radio">
                            <label>
                              <input
                                checked={field.value === '4'}
                                {...field}
                                type="radio"
                                value="4"
                                data-cy="ride-book-table-summaries-settings-parial-summaries-days"
                              />
                              <span>
                                {f({ id: 'tables.tripsOverview.summaryLines.afterDays' })}
                              </span>
                            </label>
                          </div>
                        </li>
                      </ul>
                    )}
                  />

                  <div
                    className="b-dropdown__title b-dropdown__title--border-0 mt-4"
                    title={f({
                      id: 'tables.tripsOverview.summaryLines.ridesPartialSummaries.tooltip',
                    })}
                  >
                    {f({ id: 'tables.tripsOverview.summaryLines.ridesPartialSummaries' })}
                  </div>
                  <ul className="b-dropdown__list">
                    <Controller
                      name="all"
                      control={control}
                      render={({ field }) => (
                        <li>
                          <div className={errors.all ? 'form-group-error' : ''}>
                            {errors.all && (
                              <div className={errors.all ? 'form-error-info' : ''}>
                                {errors.all.message}
                              </div>
                            )}
                            <div className="e-checkbox">
                              <label>
                                <input
                                  type="checkbox"
                                  checked={field.value}
                                  disabled={watch('interval') === '0'}
                                  data-cy="ride-book-table-summaries-settings-parial-summaries-total"
                                  onChange={(e) => {
                                    setValue('all', e.target.checked);
                                    trigger('all');
                                    trackEvent([
                                      'ride book',
                                      'click',
                                      'Select all rides together in ride book summary settings',
                                    ]);
                                  }}
                                />
                                <span>
                                  {f({
                                    id: 'tables.tripsOverview.summaryLines.ridesPartialSummaries.all',
                                  })}
                                </span>
                              </label>
                            </div>
                          </div>
                        </li>
                      )}
                    />
                    <Controller
                      name="public"
                      control={control}
                      render={({ field }) => (
                        <li>
                          <div className="e-checkbox">
                            <label>
                              <input
                                type="checkbox"
                                checked={field.value}
                                disabled={watch('interval') === '0'}
                                data-cy="ride-book-table-summaries-settings-parial-summaries-public"
                                onChange={(e) => {
                                  setValue('public', e.target.checked);
                                  trigger('all');
                                  trackEvent([
                                    'ride book',
                                    'click',
                                    'Select bussiness trips rid book summary settings',
                                  ]);
                                }}
                              />
                              <span>
                                {f({
                                  id: 'tables.tripsOverview.summaryLines.ridesPartialSummaries.public',
                                })}
                              </span>
                            </label>
                          </div>
                        </li>
                      )}
                    />
                    <Controller
                      name="private"
                      control={control}
                      render={({ field }) => (
                        <li>
                          <div className="e-checkbox">
                            <label>
                              <input
                                type="checkbox"
                                checked={field.value}
                                disabled={watch('interval') === '0'}
                                data-cy="ride-book-table-summaries-settings-parial-summaries-private"
                                onChange={(e) => {
                                  setValue('private', e.target.checked);
                                  trigger('all');
                                  trackEvent([
                                    'ride book',
                                    'click',
                                    'Select private trips ride book summary settings',
                                  ]);
                                }}
                              />
                              <span>
                                {f({
                                  id: 'tables.tripsOverview.summaryLines.ridesPartialSummaries.private',
                                })}
                              </span>
                            </label>
                          </div>
                        </li>
                      )}
                    />
                  </ul>
                </div>

                <div className="b-dropdown__footer b-dropdown__footer--border-on">
                  <button
                    onClick={handleReset}
                    title={
                      isDirty
                        ? f({ id: 'waypoints.cancel' })
                        : f({ id: 'ride-book-table-summaries-settings.cancel' })
                    }
                    className={`${
                      !isDirty ? 'e-button e-button--gray e-button--disabled' : 'e-button'
                    }`}
                    data-cy="ride-book-table-summaries-settings-reset"
                    disabled={!isDirty}
                  >
                    Reset
                  </button>
                  <button
                    onClick={handleSubmit(handleApply)}
                    title={f({ id: 'ride-book-table-summaries-settings.apply' })}
                    className={`${
                      errors?.all
                        ? 'e-button e-button--gray e-button--disabled'
                        : 'e-button e-button--gray'
                    }`}
                    data-cy="ride-book-table-summaries-settings-apply"
                  >
                    {f({ id: 'table.apply' })}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default SummarySettings;
